import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  IconButton,
  Button,
  ButtonGroup,
  Tooltip,
  CircularProgress,
  Alert,
  TextField,
  Paper,
  Divider,
} from '@mui/material';
import {
  ArrowBack,
  Save as SaveIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Preview as PreviewIcon,
} from '@mui/icons-material';
import { AIRMASK_GREEN, AIRMASK_DARK, AIRMASK_SURFACE_GRAY } from '../constants/colors';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { MarkdownRenderer } from '../components/MarkdownText';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { fileSystemService } from '../services/api';
import { FileSystemNode } from '../types/api';
import MermaidDiagram from '../components/MermaidDiagram';

type ViewMode = 'edit' | 'preview' | 'split';

// Returns all H1 titles (trimmed) found in the markdown, in document order.
// Entries for blank H1s (e.g. "# ") are empty strings.
const extractH1s = (markdown: string): string[] => {
  const matches: string[] = [];
  const re = /^#\s*(.*)/gm;
  let m: RegExpExecArray | null;
  while ((m = re.exec(markdown)) !== null) {
    matches.push(m[1].trim());
  }
  return matches;
};

const MarkdownEditorPage: React.FC = () => {
  const { fileId } = useParams<{ fileId: string }>();
  const navigate = useNavigate();

  const [file, setFile] = useState<FileSystemNode | null>(null);
  const [content, setContent] = useState('');
  const [originalContent, setOriginalContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const [viewMode, setViewMode] = useState<ViewMode>('split');

  // Tracks the last non-empty H1 seen so we can fall back to it if all H1s are cleared.
  const lastNonEmptyH1 = useRef<string>('');

  useEffect(() => {
    if (fileId) {
      loadFile(fileId);
    }
  }, [fileId]);

  useEffect(() => {
    setHasUnsavedChanges(content !== originalContent);
  }, [content, originalContent]);

  // Warn user about unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const loadFile = async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fileSystemService.getFile(id);
      if (response.success && response.data) {
        setFile(response.data);
        const loadedContent = response.data.content || '';
        setContent(loadedContent);
        setOriginalContent(loadedContent);
        // Seed the fallback with the first non-empty H1, or the file stem (name without
        // extension) if none exists. We always store the stem so that re-appending the
        // extension at save time produces the correct full name.
        const h1s = extractH1s(loadedContent);
        const nameStem = response.data.name.replace(/\.[^.]+$/, '');
        lastNonEmptyH1.current = h1s.find(h => h !== '') ?? nameStem;
      } else {
        setError(response.error || 'Failed to load file');
      }
    } catch (err) {
      setError('Failed to load file');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!fileId || !file) return;

    setSaving(true);
    setError(null);
    try {
      // H1→name sync only applies to .md files.
      const isMd = file.name.toLowerCase().endsWith('.md');

      // Preserve the file extension so renaming never strips .md.
      // H1 text never contains an extension, so we add it back explicitly.
      const ext = file.name.match(/\.[^.]+$/)?.[0] ?? '';

      // Determine what the file name should be based on the first non-empty H1.
      // Walk all H1s in document order; if the first non-empty one exists, use it.
      // If all H1s are blank (or there are none), fall back to the last known non-empty H1.
      // Both values are stems (no extension); we append ext to form the full target name.
      const h1s = isMd ? extractH1s(content) : [];
      const firstNonEmpty = h1s.find(h => h !== '');
      const targetName = (firstNonEmpty !== undefined
        ? firstNonEmpty
        : lastNonEmptyH1.current) + ext;

      const nameChanged = isMd && targetName !== file.name;
      const updatePayload: { content: string; name?: string } = { content };
      if (nameChanged) {
        updatePayload.name = targetName;
      }

      let response = await fileSystemService.updateFile(fileId, updatePayload);

      // If the rename failed due to a name conflict, retry saving content-only
      // using the existing file name (the backend will skip the rename entirely).
      if (!response.success && nameChanged) {
        response = await fileSystemService.updateFile(fileId, { content });
      }

      if (response.success) {
        setOriginalContent(content);
        setHasUnsavedChanges(false);
        // Update the local file state so the header reflects the new name.
        if (nameChanged && response.data) {
          setFile(response.data);
        }
        // Advance the fallback only after a successful save.
        if (isMd && firstNonEmpty !== undefined) {
          lastNonEmptyH1.current = firstNonEmpty;
        }
      } else {
        setError(response.error || 'Failed to save file');
      }
    } catch (err) {
      setError('Failed to save file');
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    // Check if user has unsaved changes before navigating
    if (hasUnsavedChanges) {
      if (!window.confirm('You have unsaved changes. Are you sure you want to leave?')) {
        return; // User cancelled, stay on page
      }
    }
    
    // Check if we have encoded backpack state in the URL
    // This indicates the user came from the backpack modal
    const searchParams = new URLSearchParams(window.location.search);
    const encodedState = searchParams.get('state');
    
    if (encodedState) {
      try {
        // Decode the Base64 state to restore backpack modal state
        const decodedState = JSON.parse(atob(encodedState));
        
        // Only proceed if this was from the backpack
        if (decodedState.from === 'backpack') {
          // Navigate back to the originating page with state to restore the backpack modal.
          // replace: true removes the editor entry from the browser history stack so that
          // pressing backspace / browser-back from the main page cannot navigate back into
          // a stale editor page and flash its rendered markdown content on screen.
          navigate(decodedState.basePath || '/today/log-habits', { 
            replace: true,
            state: { 
              openBackpack: true,
              expandedSections: decodedState.expanded || [],
              nestedExpanded: decodedState.nestedExpanded || [],
              expandedFolders: decodedState.expandedFolders || [],
            } 
          });
          return;
        }
      } catch (error) {
        // If state decoding fails, just navigate back normally
        console.error('Failed to decode backpack state:', error);
      }
    }
    
    // Default behavior: just go back in history
    navigate(-1);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !file) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button onClick={handleBack} startIcon={<ArrowBack />}>
          Go Back
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', bgcolor: 'background.default' }}>
      {/* Header */}
      <Paper 
        elevation={1} 
        sx={{ 
          p: 2, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          borderRadius: 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={handleBack}>
            <ArrowBack />
          </IconButton>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {file?.name || 'Untitled'}
            </Typography>
            {hasUnsavedChanges && (
              <Typography variant="caption" color="warning.main">
                Unsaved changes
              </Typography>
            )}
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {/* View Mode Toggle */}
          <ButtonGroup size="small" variant="outlined">
            <Tooltip title="Edit Mode">
              <Button
                onClick={() => setViewMode('edit')}
                variant={viewMode === 'edit' ? 'contained' : 'outlined'}
              >
                <EditIcon fontSize="small" />
              </Button>
            </Tooltip>
            <Tooltip title="Split View">
              <Button
                onClick={() => setViewMode('split')}
                variant={viewMode === 'split' ? 'contained' : 'outlined'}
              >
                <PreviewIcon fontSize="small" />
              </Button>
            </Tooltip>
            <Tooltip title="Preview Mode">
              <Button
                onClick={() => setViewMode('preview')}
                variant={viewMode === 'preview' ? 'contained' : 'outlined'}
              >
                <ViewIcon fontSize="small" />
              </Button>
            </Tooltip>
          </ButtonGroup>

          <Divider orientation="vertical" flexItem />

          {/* Save Button */}
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
            onClick={handleSave}
            disabled={!hasUnsavedChanges || saving}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </Box>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ m: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Editor Content */}
      <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {/* Edit View */}
        {(viewMode === 'edit' || viewMode === 'split') && (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              borderRight: viewMode === 'split' ? '1px solid' : 'none',
              borderColor: 'divider',
            }}
          >
            <TextField
              multiline
              fullWidth
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Write your markdown here..."
              variant="outlined"
              sx={{
                flex: 1,
                '& .MuiOutlinedInput-root': {
                  height: '100%',
                  alignItems: 'flex-start',
                  fontFamily: 'monospace',
                  fontSize: '0.9rem',
                  padding: 2,
                  '& fieldset': { border: 'none' },
                },
                '& .MuiInputBase-input': {
                  height: '100% !important',
                  overflow: 'auto !important',
                },
              }}
            />
          </Box>
        )}

        {/* Preview View */}
        {(viewMode === 'preview' || viewMode === 'split') && (
          <Box
            sx={{
              flex: 1,
              overflow: 'auto',
              p: 3,
              bgcolor: 'background.paper',
            }}
          >
            <Box
              sx={{
                maxWidth: '800px',
                margin: '0 auto',
                '& h1': { 
                  fontSize: '2rem', 
                  fontWeight: 700, 
                  mt: 3, 
                  mb: 2,
                  color: 'text.primary',
                },
                '& h2': { 
                  fontSize: '1.5rem', 
                  fontWeight: 600, 
                  mt: 2.5, 
                  mb: 1.5,
                  color: 'text.primary',
                },
                '& h3': { 
                  fontSize: '1.25rem', 
                  fontWeight: 600, 
                  mt: 2, 
                  mb: 1,
                  color: 'text.primary',
                },
                '& p': { 
                  lineHeight: 1.7, 
                  mb: 2,
                  color: 'text.primary',
                },
                '& code': {
                  bgcolor: AIRMASK_SURFACE_GRAY,
                  color: 'text.primary',
                  px: 0.5,
                  py: 0.25,
                  borderRadius: 0.5,
                  fontFamily: 'monospace',
                  fontSize: '0.875rem',
                },
                '& pre': {
                  mb: 2,
                  borderRadius: 1,
                  overflow: 'auto',
                },
                '& ul, & ol': {
                  pl: 4,
                  mb: 2,
                },
                '& li': {
                  mb: 0.5,
                },
                '& blockquote': {
                  borderLeft: '4px solid',
                  borderColor: 'primary.main',
                  pl: 2,
                  ml: 0,
                  fontStyle: 'italic',
                  color: 'text.secondary',
                },
                '& a': {
                  color: 'primary.main',
                  textDecoration: 'none',
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                },
                '& table': {
                  width: '100%',
                  borderCollapse: 'collapse',
                  mb: 2,
                },
                '& th, & td': {
                  border: '1px solid',
                  borderColor: 'divider',
                  p: 1,
                  textAlign: 'left',
                },
                '& th': {
                  bgcolor: AIRMASK_GREEN,
                  color: AIRMASK_DARK,
                  fontWeight: 600,
                },
              }}
            >
              {content ? (
                <MarkdownRenderer
                  components={{
                    code(props: any) {
                      const { children, className, node, ...rest } = props;
                      const match = /language-(\w+)/.exec(className || '');
                      if (match && match[1] === 'mermaid') {
                        return <MermaidDiagram chart={String(children)} />;
                      }
                      return match ? (
                        <SyntaxHighlighter
                          style={vscDarkPlus as any}
                          language={match[1]}
                          PreTag="div"
                        >
                          {String(children).replace(/\n$/, '')}
                        </SyntaxHighlighter>
                      ) : (
                        <code className={className} {...rest}>
                          {children}
                        </code>
                      );
                    },
                  }}
                >
                  {content}
                </MarkdownRenderer>
              ) : (
                <Typography color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  No content yet. Start writing to see the preview.
                </Typography>
              )}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default MarkdownEditorPage;
